import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { awards } from '../data/awards';
import { FaTrophy } from 'react-icons/fa';

const Awards: React.FC = () => {
  const { t } = useLanguage();
  
  const getAwardColor = (title: string) => {
    if (title.includes('대상') || title.includes('Grand Prize')) return 'from-yellow-500 to-orange-500';
    if (title.includes('우수상') || title.includes('Excellence')) return 'from-blue-500 to-purple-500';
    if (title.includes('장려상') || title.includes('Encouragement')) return 'from-green-500 to-teal-500';
    return 'from-gray-500 to-gray-600';
  };

  const getAwardIconColor = (title: string) => {
    if (title.includes('대상') || title.includes('Grand Prize')) return 'text-yellow-500';
    if (title.includes('우수상') || title.includes('Excellence')) return 'text-blue-500';
    if (title.includes('장려상') || title.includes('Encouragement')) return 'text-green-500';
    return 'text-gray-500';
  };

  if (!awards || awards.length === 0) {
    return (
      <section id="awards" className="py-20 px-6 bg-dark-surface">
        <div className="container mx-auto max-w-6xl text-center">
          <p className="text-gray-400">수상 내역이 없습니다.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="awards" className="py-20 px-6 bg-dark-surface min-h-[400px]">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold mb-4">
            <span className="text-gradient">{t('awards.title')}</span>
          </h2>
          <p className="text-gray-400">{t('awards.subtitle')}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {awards.map((award, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-dark-card rounded-lg p-6 shadow-xl relative overflow-hidden group"
            >
              <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${getAwardColor(award.title)} opacity-10 rounded-full blur-3xl group-hover:opacity-20 transition-opacity`}></div>
              
              <div className="relative z-10">
                <div className="flex items-center mb-4">
                  <FaTrophy className={`text-4xl ${getAwardIconColor(award.title)}`} />
                  <div className="ml-4">
                    <h3 className={`text-2xl font-bold bg-gradient-to-r ${getAwardColor(award.title)} bg-clip-text text-transparent`}>
                      {award.title}
                    </h3>
                    <p className="text-sm text-gray-400">{award.date}</p>
                  </div>
                </div>
                
                <h4 className="text-lg font-semibold text-white mb-2">
                  {award.organization}
                </h4>
                
                <p className="text-gray-300 mb-2">{award.description}</p>
                
                {award.project && (
                  <span className="inline-block px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
                    {award.project}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Awards;
